"use client";

import { c as _c } from "react/compiler-runtime";
import CommonTableSortIcon from "@/components/user/utils/icons/CommonTableSortIcon";
import type { SortDirection } from "@/entities/ISort";
import { useAnimate } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CommonSelectProps } from "../CommonSelect";
import CommonDropdownItem from "./CommonDropdownItem";
import CommonDropdownItemsWrapper from "./CommonDropdownItemsWrapper";
function getEditorialButtonStateClass(disabled: boolean) {
  if (disabled) {
    return "tw-cursor-not-allowed tw-text-iron-500 tw-opacity-50";
  }
  return "tw-text-iron-300 hover:tw-text-iron-100";
}
function getEditorialUnderlineClass(isOpen: boolean, disabled: boolean) {
  if (isOpen && !disabled) {
    return "after:tw-scale-x-100 after:tw-bg-primary-400";
  }
  return "after:tw-scale-x-0 after:tw-bg-iron-700 hover:after:tw-scale-x-100";
}
function getDefaultButtonStateClass(disabled: boolean) {
  if (disabled) {
    return "tw-text-iron-400 tw-opacity-50";
  }
  return "tw-text-iron-300 hover:tw-ring-iron-700";
}
function getDefaultThemeClass(theme: string) {
  if (theme === "dark") {
    return "tw-bg-iron-800 lg:tw-bg-iron-900";
  }
  return "tw-bg-iron-800";
}
function getDefaultSizeClass(size: string) {
  if (size === "md") {
    return "tw-py-3 tw-text-sm";
  }
  if (size === "tabs") {
    return "tw-py-[11px] tw-text-sm";
  }
  return "tw-py-2.5 tw-text-xs";
}
export default function CommonDropdown(props) {
  const $ = _c(84);
  const {
    items,
    activeItem,
    noneLabel,
    filterLabel,
    setSelected,
    dynamicPosition: t0,
    disabled: t1,
    theme: t2,
    size: t3,
    variant: t4,
    renderItemChildren,
    closeOnSelect: t5,
    showFilterLabel: t6,
    menuMinWidth
  } = props;
  const dynamicPosition = t0 === undefined ? true : t0;
  const disabled = t1 === undefined ? false : t1;
  const theme = t2 === undefined ? "dark" : t2;
  const size = t3 === undefined ? "md" : t3;
  const variant = t4 === undefined ? "default" : t4;
  const closeOnSelect = t5 === undefined ? true : t5;
  const showFilterLabel = t6 === undefined ? false : t6;
  const [isOpen, setIsOpen] = useState(false);
  const [iconScope, animateIcon] = useAnimate();
  const isEditorial = variant === "editorial";
  let t7;
  let t8;
  if ($[0] !== animateIcon || $[1] !== iconScope || $[2] !== isEditorial || $[3] !== isOpen) {
    t7 = () => {
      if (!iconScope.current) {
        return;
      }
      if (isEditorial) {
        animateIcon(iconScope.current, {
          rotate: isOpen ? 180 : 0
        });
      } else {
        if (isOpen) {
          animateIcon(iconScope.current, {
            rotate: 0
          });
        } else {
          animateIcon(iconScope.current, {
            rotate: -90
          });
        }
      }
    };
    t8 = [animateIcon, iconScope, isEditorial, isOpen];
    $[0] = animateIcon;
    $[1] = iconScope;
    $[2] = isEditorial;
    $[3] = isOpen;
    $[4] = t7;
    $[5] = t8;
  } else {
    t7 = $[4];
    t8 = $[5];
  }
  useEffect(t7, t8);
  let t9;
  if ($[6] !== activeItem || $[7] !== items) {
    let t10;
    if ($[9] !== activeItem) {
      t10 = item => item.value === activeItem;
      $[9] = activeItem;
      $[10] = t10;
    } else {
      t10 = $[10];
    }
    t9 = items.find(t10);
    $[6] = activeItem;
    $[7] = items;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const activeItemMatch = t9;
  const computedLabel = activeItemMatch?.mobileLabel ?? activeItemMatch?.label ?? noneLabel ?? "None Selected";
  let t10;
  if ($[11] !== props) {
    const getSortDirection = () => "sortDirection" in props ? props.sortDirection : undefined;
    t10 = getSortDirection();
    $[11] = props;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const sortDirection = t10;
  let t11;
  if ($[13] !== closeOnSelect || $[14] !== setSelected) {
    t11 = item_0 => {
      setSelected(item_0);
      if (closeOnSelect) {
        setIsOpen(false);
      }
    };
    $[13] = closeOnSelect;
    $[14] = setSelected;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const onSelect = t11;
  const buttonRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const rootClassName = isEditorial ? "tailwind-scope tw-inline-flex tw-h-full tw-max-w-full" : "tailwind-scope tw-h-full tw-w-full";
  const triggerWrapperClassName = isEditorial ? "tw-relative tw-inline-flex tw-max-w-full" : "tw-relative tw-w-full";
  let t12;
  if ($[16] !== disabled || $[17] !== isOpen) {
    t12 = getEditorialUnderlineClass(isOpen, disabled);
    $[16] = disabled;
    $[17] = isOpen;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const editorialUnderlineClass = t12;
  let t13;
  if ($[19] !== disabled || $[20] !== editorialUnderlineClass || $[21] !== isEditorial || $[22] !== size || $[23] !== theme) {
    t13 = isEditorial ? `${getEditorialButtonStateClass(disabled)} ${editorialUnderlineClass} tw-group tw-relative tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-0.5 tw-py-1 tw-text-left tw-text-sm tw-font-semibold tw-leading-5 tw-caret-primary-400 tw-shadow-none tw-transition tw-duration-200 tw-ease-out after:tw-absolute after:-tw-bottom-0.5 after:tw-left-0 after:tw-h-px after:tw-w-full after:tw-origin-left after:tw-transition-transform after:tw-duration-200 after:tw-content-[''] focus:tw-outline-none focus-visible:tw-outline-none focus-visible:tw-text-iron-100` : `${getDefaultButtonStateClass(disabled)} ${getDefaultThemeClass(theme)} ${getDefaultSizeClass(size)} tw-relative tw-block tw-w-full tw-justify-between tw-truncate tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-pl-3.5 tw-pr-8 tw-text-left tw-font-semibold tw-leading-5 tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-800 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400`;
    $[19] = disabled;
    $[20] = editorialUnderlineClass;
    $[21] = isEditorial;
    $[22] = size;
    $[23] = theme;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const buttonClasses = t13;
  const labelClassName = isEditorial ? "tw-shrink-0 tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-leading-5 tw-text-iron-500" : "tw-font-semibold tw-text-iron-500";
  const valueClassName = isEditorial ? "tw-truncate tw-text-iron-200 group-hover:tw-text-white" : "";
  const chevronClassName = isEditorial ? "tw-pointer-events-none tw-inline-flex tw-shrink-0 tw-items-center tw-text-iron-500 group-hover:tw-text-iron-300" : "tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 -tw-mr-1 tw-flex tw-items-center tw-pr-3.5";
  const t14 = `${filterLabel}: ${computedLabel}`;
  let t15;
  if ($[25] !== isOpen) {
    t15 = () => setIsOpen(!isOpen);
    $[25] = isOpen;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = isEditorial ? "tw-flex tw-min-w-0 tw-items-center tw-gap-1.5" : "";
  let t17;
  if ($[27] !== filterLabel || $[28] !== isEditorial || $[29] !== labelClassName || $[30] !== showFilterLabel) {
    t17 = showFilterLabel && <span className={labelClassName}>{filterLabel}:{isEditorial ? "" : " "}</span>;
    $[27] = filterLabel;
    $[28] = isEditorial;
    $[29] = labelClassName;
    $[30] = showFilterLabel;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== computedLabel || $[33] !== valueClassName) {
    t18 = <span className={valueClassName}>{computedLabel}</span>;
    $[32] = computedLabel;
    $[33] = valueClassName;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t16 || $[36] !== t17 || $[37] !== t18) {
    t19 = <span className={t16}>{t17}{t18}</span>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== sortDirection) {
    t20 = sortDirection && <span className="-tw-mt-0.5 tw-ml-2"><CommonTableSortIcon direction={sortDirection} isActive={true} /></span>;
    $[39] = sortDirection;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== iconScope) {
    t22 = <svg ref={iconScope} className="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">{t21}</svg>;
    $[42] = iconScope;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== chevronClassName || $[45] !== t22) {
    t23 = <span className={chevronClassName}>{t22}</span>;
    $[44] = chevronClassName;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== buttonClasses || $[48] !== disabled || $[49] !== isOpen || $[50] !== t14 || $[51] !== t15 || $[52] !== t19 || $[53] !== t20 || $[54] !== t23) {
    t24 = <button ref={buttonRef} type="button" aria-haspopup="true" aria-label={t14} aria-expanded={isOpen} onClick={t15} disabled={disabled} className={buttonClasses}>{t19}{t20}{t23}</button>;
    $[47] = buttonClasses;
    $[48] = disabled;
    $[49] = isOpen;
    $[50] = t14;
    $[51] = t15;
    $[52] = t19;
    $[53] = t20;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24 || $[57] !== triggerWrapperClassName) {
    t25 = <div className={triggerWrapperClassName}>{t24}</div>;
    $[56] = t24;
    $[57] = triggerWrapperClassName;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  const t26 = isEditorial ? "right" : "auto";
  let t27;
  if ($[59] !== activeItem || $[60] !== isMobile || $[61] !== items || $[62] !== onSelect || $[63] !== renderItemChildren || $[64] !== sortDirection) {
    let t28;
    if ($[66] !== activeItem || $[67] !== isMobile || $[68] !== items.length || $[69] !== onSelect || $[70] !== renderItemChildren || $[71] !== sortDirection) {
      t28 = (item_1, i) => <CommonDropdownItem key={item_1.key} item={item_1} itemIdx={i} totalItems={items.length} activeItem={activeItem} sortDirection={sortDirection} isMobile={isMobile} setSelected={onSelect}>{renderItemChildren?.(item_1)}</CommonDropdownItem>;
      $[66] = activeItem;
      $[67] = isMobile;
      $[68] = items.length;
      $[69] = onSelect;
      $[70] = renderItemChildren;
      $[71] = sortDirection;
      $[72] = t28;
    } else {
      t28 = $[72];
    }
    t27 = items.map(t28);
    $[59] = activeItem;
    $[60] = isMobile;
    $[61] = items;
    $[62] = onSelect;
    $[63] = renderItemChildren;
    $[64] = sortDirection;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[73] !== dynamicPosition || $[74] !== filterLabel || $[75] !== isOpen || $[76] !== menuMinWidth || $[77] !== t26 || $[78] !== t27) {
    t28 = <CommonDropdownItemsWrapper isOpen={isOpen} setOpen={setIsOpen} buttonRef={buttonRef} filterLabel={filterLabel} dynamicPosition={dynamicPosition} horizontalAlign={t26} minWidth={menuMinWidth} onIsMobile={setIsMobile}>{t27}</CommonDropdownItemsWrapper>;
    $[73] = dynamicPosition;
    $[74] = filterLabel;
    $[75] = isOpen;
    $[76] = menuMinWidth;
    $[77] = t26;
    $[78] = t27;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== rootClassName || $[81] !== t25 || $[82] !== t28) {
    t29 = <div className={rootClassName}>{t25}{t28}</div>;
    $[80] = rootClassName;
    $[81] = t25;
    $[82] = t28;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  return t29;
}